import { Button as MuiButton } from "@mui/material";
import type {
  ElementType,
  MouseEventHandler,
  PropsWithChildren,
  ReactNode,
} from "react";

export type ButtonVariant = "contained" | "text" | "outlined";
export type ButtonColor = "primary" | "error" | "success" | "info" | "whatsapp";
export type ButtonSize = "small" | "medium" | "large";
export type ButtonType = "button" | "submit" | "reset";
export type ButtonBorderRoudness = "medium";

export type ButtonProps = PropsWithChildren<{
  variant?: ButtonVariant;
  color?: ButtonColor;
  size?: ButtonSize;
  fullWidth?: boolean;
  disabled?: boolean;
  loading?: boolean;
  type?: ButtonType;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  href?: string;
  borderRoundness?: ButtonBorderRoudness;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  // Renders the button as a different element — use a non-interactive element
  // (e.g. "span") only inside another interactive element (e.g. ButtonBase)
  // to avoid invalid DOM nesting
  as?: ElementType;
}>;

export function Button({
  children,
  variant,
  color,
  size,
  fullWidth,
  disabled,
  loading,
  type,
  startIcon,
  endIcon,
  href,
  borderRoundness,
  onClick,
  as,
  ...rest
}: ButtonProps) {
  return (
    <MuiButton
      variant={variant}
      color={color}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      loading={loading}
      disableElevation
      type={type}
      startIcon={startIcon}
      endIcon={endIcon}
      href={href}
      onClick={onClick}
      sx={
        borderRoundness === "medium"
          ? { borderRadius: "var(--radius-2xl)" }
          : undefined
      }
      {...(as ? { component: as, role: undefined } : {})}
      {...rest}
    >
      {children}
    </MuiButton>
  );
}
